/**
 * ResourceFilter Component
 *
 * Dropdown filter for selecting a specific resource or viewing all resources.
 * Resources are grouped by agent name using <optgroup> elements.
 *
 * Validates: Requirements 4.1, 4.2, 4.3, 7.3
 * - Requirement 4.1: Resource filter dropdown populated with all resources from calendar-active agents.
 * - Requirement 4.2: Selecting a resource filters bookings to that resource only.
 * - Requirement 4.3: "Todos los recursos" shows all bookings.
 * - Requirement 7.3: Resources grouped by agent name in the dropdown.
 */

import React from 'react';
import { tokens } from '../../../design-system/tokens';
import type { CalendarViewResource } from '../calendarView.types';

// ============================================
// TYPES
// ============================================

export interface ResourceFilterProps {
  /** Available resources to display in the dropdown */
  resources: CalendarViewResource[];
  /** Currently selected resource ID, or null for "all" */
  selectedResourceId: string | null;
  /** Callback when the selection changes; null means "all resources" */
  onChange: (resourceId: string | null) => void;
}

// ============================================
// STYLES
// ============================================

const selectStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: `${tokens.spacing[1]} ${tokens.spacing[3]}`,
  border: `1px solid ${tokens.colors.neutral[300]}`,
  borderRadius: tokens.borderRadius.md,
  backgroundColor: tokens.colors.neutral[50],
  color: tokens.colors.neutral[700],
  fontSize: tokens.typography.fontSize.sm,
  fontWeight: tokens.typography.fontWeight.medium,
  cursor: 'pointer',
  transition: `background-color ${tokens.transitions.fast}, border-color ${tokens.transitions.fast}`,
  outline: 'none',
  fontFamily: tokens.typography.fontFamily.sans,
};

// ============================================
// HELPERS
// ============================================

/**
 * Groups resources by their agentName for rendering as <optgroup> elements.
 */
function groupResourcesByAgent(resources: CalendarViewResource[]): Map<string, CalendarViewResource[]> {
  const groups = new Map<string, CalendarViewResource[]>();
  for (const resource of resources) {
    const existing = groups.get(resource.agentName);
    if (existing) {
      existing.push(resource);
    } else {
      groups.set(resource.agentName, [resource]);
    }
  }
  return groups;
}

// ============================================
// MAIN COMPONENT
// ============================================

/**
 * ResourceFilter — Dropdown to filter calendar bookings by resource.
 *
 * Renders a <select> element with:
 * - Default option "Todos los recursos" (maps to null)
 * - Resources grouped by agent name using <optgroup>
 *
 * @example
 * ```tsx
 * <ResourceFilter
 *   resources={resources}
 *   selectedResourceId={selectedId}
 *   onChange={(id) => setSelectedId(id)}
 * />
 * ```
 */
export function ResourceFilter({ resources, selectedResourceId, onChange }: ResourceFilterProps) {
  const groupedResources = groupResourcesByAgent(resources);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    onChange(value === '' ? null : value);
  };

  return (
    <select
      value={selectedResourceId ?? ''}
      onChange={handleChange}
      aria-label="Filtrar por recurso"
      style={selectStyle}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = tokens.colors.neutral[200];
        e.currentTarget.style.borderColor = tokens.colors.neutral[400];
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = tokens.colors.neutral[50];
        e.currentTarget.style.borderColor = tokens.colors.neutral[300];
      }}
    >
      <option value="">Todos los recursos</option>
      {Array.from(groupedResources.entries()).map(([agentName, agentResources]) => (
        <optgroup key={agentName} label={agentName}>
          {agentResources.map((resource) => (
            <option key={resource.id} value={resource.id}>
              {resource.name}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

export default ResourceFilter;
